/**
 * Authentication Service
 * Handles all authentication-related API calls and token management
 */

import config from '../config/environment';
import type {
  User,
  AuthTokens,
  LoginCredentials,
  RegisterData,
  ChangePasswordData,
  UpdateProfileData,
  APIResponse,
  AuthResponse,
  RefreshTokenResponse,
  ProfileResponse
} from '../types/auth';
import {
  AUTH_STORAGE_KEYS,
  AUTH_ENDPOINTS
} from '../types/auth';

class AuthService {
  private baseUrl: string = config.api.baseUrl;

  /**
   * Make authenticated API request with automatic token refresh
   */
  private async makeRequest<T>(
    url: string,
    options: RequestInit = {}
  ): Promise<APIResponse<T>> {
    const accessToken = this.getAccessToken();
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string> || {})
    };

    if (accessToken) {
      headers.Authorization = `Bearer ${accessToken}`;
    }

    try {
      const response = await fetch(`${this.baseUrl}${url}`, {
        ...options,
        headers
      });

      const data: APIResponse<T> = await response.json();

      // If token expired, try to refresh
      if (!response.ok && response.status === 401 && accessToken) {
        const refreshed = await this.refreshToken();
        if (refreshed) {
          // Retry with new token
          headers.Authorization = `Bearer ${this.getAccessToken()}`;
          const retryResponse = await fetch(`${this.baseUrl}${url}`, {
            ...options,
            headers
          });
          return await retryResponse.json();
        }
      }

      return data;
    } catch (error) {
      throw new Error(`Network error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Register new user
   */
  async register(userData: RegisterData): Promise<APIResponse<AuthResponse>> {
    try {
      const response = await fetch(`${this.baseUrl}${AUTH_ENDPOINTS.REGISTER}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(userData)
      });

      const data: APIResponse<AuthResponse> = await response.json();

      if (data.success && data.data) {
        this.setTokens(data.data.tokens);
        this.setUser(data.data.user);
      }

      return data;
    } catch (error) {
      throw new Error(`Registration failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Login user
   */
  async login(credentials: LoginCredentials): Promise<APIResponse<AuthResponse>> {
    try {
      const response = await fetch(`${this.baseUrl}${AUTH_ENDPOINTS.LOGIN}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(credentials)
      });

      const data: APIResponse<AuthResponse> = await response.json();

      if (data.success && data.data) {
        this.setTokens(data.data.tokens);
        this.setUser(data.data.user);
      }

      return data;
    } catch (error) {
      throw new Error(`Login failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Logout user
   */
  async logout(): Promise<APIResponse> {
    try {
      const response = await this.makeRequest(AUTH_ENDPOINTS.LOGOUT, {
        method: 'POST'
      });

      // Clear local storage regardless of response
      this.clearAuthData();

      return response;
    } catch (error) {
      // Clear local storage even if request fails
      this.clearAuthData();
      throw new Error(`Logout failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Get current user profile
   */
  async getProfile(): Promise<APIResponse<ProfileResponse>> {
    return this.makeRequest<ProfileResponse>(AUTH_ENDPOINTS.PROFILE);
  }

  /**
   * Update user profile
   */
  async updateProfile(profileData: UpdateProfileData): Promise<APIResponse<ProfileResponse>> {
    return this.makeRequest<ProfileResponse>(AUTH_ENDPOINTS.UPDATE_PROFILE, {
      method: 'PUT',
      body: JSON.stringify(profileData)
    });
  }

  /**
   * Change password
   */
  async changePassword(passwordData: ChangePasswordData): Promise<APIResponse> {
    return this.makeRequest(AUTH_ENDPOINTS.CHANGE_PASSWORD, {
      method: 'POST',
      body: JSON.stringify(passwordData)
    });
  }

  /**
   * Refresh access token
   */
  async refreshToken(): Promise<boolean> {
    try {
      const refreshToken = this.getRefreshToken();
      if (!refreshToken) {
        return false;
      }

      const response = await fetch(`${this.baseUrl}${AUTH_ENDPOINTS.REFRESH_TOKEN}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ refreshToken })
      });

      const data: APIResponse<RefreshTokenResponse> = await response.json();

      if (data.success && data.data) {
        this.setTokens(data.data.tokens);
        return true;
      }

      return false;
    } catch (error) {
      console.error('Token refresh failed:', error);
      return false;
    }
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    const token = this.getAccessToken();
    const user = this.getUser();
    return !!(token && user);
  }

  /**
   * Get access token from storage
   */
  getAccessToken(): string | null {
    return localStorage.getItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN);
  }

  /**
   * Get refresh token from storage
   */
  getRefreshToken(): string | null {
    return localStorage.getItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN);
  }

  /**
   * Get user data from storage
   */
  getUser(): User | null {
    try {
      const userData = localStorage.getItem(AUTH_STORAGE_KEYS.USER_DATA);
      return userData ? JSON.parse(userData) : null;
    } catch (error) {
      console.error('Error parsing user data:', error);
      return null;
    }
  }

  /**
   * Set tokens in storage
   */
  setTokens(tokens: AuthTokens): void {
    localStorage.setItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN, tokens.accessToken);
    localStorage.setItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN, tokens.refreshToken);
  }

  /**
   * Set user data in storage
   */
  setUser(user: User): void {
    localStorage.setItem(AUTH_STORAGE_KEYS.USER_DATA, JSON.stringify(user));
  }

  /**
   * Clear all authentication data
   */
  clearAuthData(): void {
    localStorage.removeItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN);
    localStorage.removeItem(AUTH_STORAGE_KEYS.REFRESH_TOKEN);
    localStorage.removeItem(AUTH_STORAGE_KEYS.USER_DATA);
  }

  /**
   * Initialize auth state from storage on app start
   */
  initializeAuth(): { user: User | null; tokens: AuthTokens | null } {
    const user = this.getUser();
    const accessToken = this.getAccessToken();
    const refreshToken = this.getRefreshToken();

    const tokens = accessToken && refreshToken ? { accessToken, refreshToken } : null;

    return { user, tokens };
  }
}

// Export singleton instance
export const authService = new AuthService();
export default authService;